import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import InventoryAlerts from '../components/InventoryAlerts';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay from '../components/CurrencyDisplay';
import PendingApprovalsBar from '../components/PendingApprovalsBar';
import PendingMembershipsWidget from '../components/PendingMembershipsWidget';
// UnlinkedRevenuesWidget moved to /weekly-loyalty-reports page
import DeletedRevenuesWidget from '../components/DeletedRevenuesWidget';
import useWebSocket from '../hooks/useWebSocket';
import { 
  RealtimeNotifications, 
  ConnectionStatus, 
  OnlineUsersIndicator 
} from '../components/RealtimeComponents';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Award,
  Activity,
  UserCheck
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [showAlertPopup, setShowAlertPopup] = useState(false);
  
  // WebSocket for real-time updates
  const { 
    isConnected, 
    onlineUsers, 
    realtimeStats, 
    notifications, 
    dismissNotification 
  } = useWebSocket(user);

  useEffect(() => {
    fetchDashboardData();
    // Show alert popup on first load
    const hasSeenAlert = sessionStorage.getItem('inventory_alert_shown');
    if (!hasSeenAlert) {
      setShowAlertPopup(true);
      sessionStorage.setItem('inventory_alert_shown', 'true');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Update stats with real-time data
  useEffect(() => {
    if (realtimeStats && stats) {
      setStats(prev => ({
        ...prev,
        today_revenue: realtimeStats.revenue_today || prev?.today_revenue,
        today_expenses: realtimeStats.expenses_today || prev?.today_expenses,
        queue_count: realtimeStats.queue_count
      }));
    }
  }, [realtimeStats]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, revenuesRes] = await Promise.all([
        api.get('/statistics/dashboard'),
        api.get('/revenues')
      ]);
      
      setStats(statsRes.data);
      
      // Prepare chart data
      const last7Days = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayRevenues = revenuesRes.data.filter(r => 
          r.date.split('T')[0] === dateStr
        );
        
        const totalRevenue = dayRevenues.reduce((sum, r) => sum + r.amount, 0);
        const totalClients = dayRevenues.reduce((sum, r) => sum + r.client_count, 0);
        
        last7Days.push({
          date: dateStr,
          revenue: totalRevenue,
          clients: totalClients,
          name: date.toLocaleDateString('ar-SA', { weekday: 'short' })
        });
      }
      
      setRevenueData(last7Days);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatRange = (start, end) => {
    if (!start || !end) return '';
    const fmt = (s) => {
      try {
        return new Date(s).toLocaleDateString('ar-EG', { day: '2-digit', month: 'short' });
      } catch (e) { return s; }
    };
    return `${fmt(start)} → ${fmt(end)}`;
  };
  const weekRangeLabel = formatRange(stats?.week_start, stats?.week_end);

  const computeChange = (current, previous) => {
    const c = Number(current) || 0;
    const p = Number(previous) || 0;
    if (p === 0 && c === 0) return { pct: 0, dir: 'flat' };
    if (p === 0) return { pct: null, dir: c > 0 ? 'up' : (c < 0 ? 'down' : 'flat') };
    const pct = ((c - p) / Math.abs(p)) * 100;
    return { pct, dir: pct > 0 ? 'up' : (pct < 0 ? 'down' : 'flat') };
  };

  const renderTrend = (change, positiveIsGood = true) => {
    if (!change) return null;
    const { pct, dir } = change;
    const goodColor = 'text-green-500';
    const badColor = 'text-red-500';
    const flatColor = 'text-muted-foreground';
    let color = flatColor;
    if (dir === 'up') color = positiveIsGood ? goodColor : badColor;
    else if (dir === 'down') color = positiveIsGood ? badColor : goodColor;
    const arrow = dir === 'up' ? '▲' : (dir === 'down' ? '▼' : '■');
    const pctText = pct === null ? '—' : `${Math.abs(pct).toFixed(1)}%`;
    return (
      <span className={`inline-flex items-center gap-1 text-xs ${color}`}>
        <span>{arrow}</span>
        <span>{pctText}</span>
        <span className="text-muted-foreground">vs الأسبوع السابق</span>
      </span>
    );
  };

  const revenueChange = computeChange(stats?.total_revenue, stats?.previous_week_revenue);
  const expensesChange = computeChange(stats?.total_expenses, stats?.previous_week_expenses);
  const profitChange = computeChange(stats?.net_profit, stats?.previous_week_net_profit);

  const commitmentsConfigured = stats?.commitments_configured;
  const commitmentsWarningText = !commitmentsConfigured
    ? '⚠️ لم يتم إدراج الالتزامات الشهرية بعد — يرجى من المدير إضافتها لاحتساب الصافي بدقة'
    : null;
  const isAdminUser = user?.role === 'admin';
  const netProfitSubtitleNode = commitmentsWarningText ? (
    <div className="text-xs text-yellow-500 mt-2 flex flex-col items-start gap-1.5" data-testid="commitments-missing-warning">
      <span>{commitmentsWarningText}</span>
      {isAdminUser && (
        <button
          type="button"
          onClick={() => navigate('/fixed-commitments')}
          data-testid="manage-commitments-btn"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-500/40 transition-colors"
        >
          <span>إدارة الالتزامات الثابتة</span>
          <span aria-hidden="true">←</span>
        </button>
      )}
    </div>
  ) : (
    stats?.weekly_commitments_share != null ? (
      <p className="text-xs text-muted-foreground mt-2">
        {`بعد خصم الحصة الأسبوعية للالتزامات الثابتة (${Number(stats.weekly_commitments_share).toFixed(2)} د.إ)`}
      </p>
    ) : null
  );

  const statCards = [
    {
      title: t('totalRevenue'),
      value: <CurrencyDisplay amount={stats?.total_revenue || 0} className="text-green-500" color="green" />,
      icon: TrendingUp,
      color: 'text-green-500',
      bg: 'bg-green-500/10',
      subtitle: weekRangeLabel ? `الأسبوع الحالي (السبت → الجمعة): ${weekRangeLabel}` : null,
      trend: renderTrend(revenueChange, true)
    },
    {
      title: t('totalExpenses'),
      value: <CurrencyDisplay amount={stats?.total_expenses || 0} className="text-red-500" color="red" />,
      icon: TrendingDown,
      color: 'text-red-500',
      bg: 'bg-red-500/10',
      subtitle: weekRangeLabel ? `الأسبوع الحالي (السبت → الجمعة): ${weekRangeLabel}` : null,
      trend: renderTrend(expensesChange, false)
    },
    {
      title: t('netProfit'),
      value: <CurrencyDisplay amount={stats?.net_profit || 0} className="text-primary" color="primary" />,
      icon: () => <DirhamIconColored className="w-5 h-5" color="primary" />,
      color: 'text-primary',
      bg: 'bg-primary/10',
      subtitleNode: netProfitSubtitleNode,
      trend: renderTrend(profitChange, true)
    },
    {
      title: t('todayClients'),
      value: stats?.today_clients || 0,
      icon: Users,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10'
    },
    {
      title: t('weeklyAverage'),
      value: <CurrencyDisplay amount={stats?.weekly_average || 0} className="text-cyan-500" color="cyan" />,
      icon: Activity,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10'
    },
    {
      title: t('averagePerHaircut'),
      value: <CurrencyDisplay amount={stats?.average_per_haircut || 0} className="text-primary" color="primary" />,
      icon: Award,
      color: 'text-primary',
      bg: 'bg-primary/10'
    }
  ];
  
  // Add online users card for admin
  if (user?.role === 'admin') {
    statCards.push({
      title: 'المتصلون الآن',
      value: realtimeStats?.online_users?.total || onlineUsers?.length || 0,
      icon: UserCheck,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      isLive: isConnected
    });
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Real-time Notifications */}
      <RealtimeNotifications 
        notifications={notifications} 
        onDismiss={dismissNotification} 
      />
      
      {/* Inventory Alert Popup */}
      {showAlertPopup && (
        <InventoryAlerts showPopup={true} onClose={() => setShowAlertPopup(false)} />
      )}

      {/* Approval Hub — admin only, hidden when nothing is pending */}
      {user?.role === 'admin' && <PendingApprovalsBar />}

      {/* Pending Loyalty Memberships Summary — admin only */}
      {user?.role === 'admin' && <PendingMembershipsWidget />}

      {/* Recent unlinked revenues reminder — moved to /weekly-loyalty-reports */}

      {/* Soft-deleted revenues (7-day undo window) — admin only */}
      {user?.role === 'admin' && <DeletedRevenuesWidget />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
            {t('dashboard')}
          </h1>
          <p className="text-muted-foreground">{t('welcome')}, {user?.name}</p>
        </div>
        
        {/* Real-time Status Bar */}
        <div className="flex items-center gap-3">
          <ConnectionStatus isConnected={isConnected} />
          {user?.role === 'admin' && (
            <OnlineUsersIndicator 
              onlineUsers={onlineUsers} 
              count={realtimeStats?.online_users} 
            />
          )}
        </div>
      </div>

      {/* Inventory Alerts Widget */}
      <InventoryAlerts />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const isFunction = typeof Icon === 'function' && !Icon.prototype?.isReactComponent && Icon.toString().includes('=>');
          return (
            <div
              key={stat.label || `stat-${index}`}
              data-testid={`stat-card-${index}`}
              className="bg-card border border-white/5 rounded-xl shadow-2xl backdrop-blur-sm p-6 hover:border-primary/20 transition-colors duration-300 relative overflow-hidden"
            >
              {/* Live indicator for real-time cards */}
              {stat.isLive && isConnected && (
                <div className="absolute top-3 left-3">
                  <span className="flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.bg}`}>
                  {isFunction ? <Icon /> : <Icon className={`w-6 h-6 ${stat.color}`} />}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  {stat.title}
                </p>
                <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                {stat.trend && (
                  <div className="mt-2" data-testid={`stat-card-${index}-trend`}>
                    {stat.trend}
                  </div>
                )}
                {stat.subtitleNode ? (
                  stat.subtitleNode
                ) : stat.subtitle ? (
                  <p className={`text-xs mt-2 ${stat.subtitleClass || 'text-muted-foreground'}`} data-testid={`stat-card-${index}-subtitle`}>
                    {stat.subtitle}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Top Barber */}
      {stats?.top_barber && (
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl backdrop-blur-sm p-6">
          <h3 className="text-xl font-semibold mb-4">{t('topBarber')}</h3>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Award className="w-8 h-8 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.top_barber}</p>
              <p className="text-sm text-muted-foreground">{t('topBarber')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Chart */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl backdrop-blur-sm p-6">
        <h3 className="text-xl font-semibold mb-6">{t('revenueChart')} - {t('weekly')}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={revenueData}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis 
              dataKey="name" 
              stroke="#a1a1aa"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#a1a1aa"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#18181b',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#fafafa'
              }}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#D4AF37"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Clients Chart */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl backdrop-blur-sm p-6">
        <h3 className="text-xl font-semibold mb-6">{t('clients')} - {t('weekly')}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis 
              dataKey="name" 
              stroke="#a1a1aa"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#a1a1aa"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#18181b',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#fafafa'
              }}
            />
            <Bar dataKey="clients" fill="#D4AF37" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;
